/**
 * @fileoverview Redux slice for companies list state management
 * @active Used in current Next.js App Router implementation
 * 
 * This slice manages the state for the companies list view, including:
 * - Companies data with deduplication
 * - Selected companies for batch operations
 * - Column visibility settings
 * - Loading states
 * - View mode (cards vs table)
 * 
 * @see {@link src/components/companies/companiesList/newCompaniesList/types.ts} - Type definitions for company data
 * @see {@link src/store/newCompaniesList/newCompaniesList.selectors.ts} - Selectors for accessing this state
 * @see {@link src/services/newCompaniesList.service.ts} - API service that dispatches actions to this slice
 * @see {@link src/hooks/useCompaniesListRequest.ts} - Hook that uses this slice for data management
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Main component using this state
 * @see {@link src/store/companiesList/companiesList.slice.js} - Legacy slice being replaced by this implementation
 */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { listFields } from "@/components/companies/companiesList/newCompaniesList/data/lists";
import type { Company } from "@/types/company";

// Define the state shape with explicit types
interface CompaniesListState {
  companiesList: Company[];
  selectedCompanies: Company[];
  columnsField: Array<string>; // String array for column IDs
  isCompaniesLoading: boolean;
  searchText: string | undefined;
  isCompaniesCards: boolean;
}

// Safely extract column IDs from listFields
const getInitialColumns = (): string[] => {
  if (!listFields || !Array.isArray(listFields)) return [];
  
  const fields = listFields.slice(1);
  if (!fields || !Array.isArray(fields)) return [];
  
  const columnIds = fields.map(f => f?.id).filter(Boolean);
  return columnIds as string[];
};

const initialState: CompaniesListState = {
  companiesList: [],
  selectedCompanies: [],
  columnsField: getInitialColumns(),
  isCompaniesLoading: false,
  searchText: undefined,
  isCompaniesCards: false,
};

/**
 * Helper function to prevent duplicate items in arrays
 * @param arr - Current array of items
 * @param action - New items to add
 * @returns Array of items that don't already exist in the original array
 */
const noRepeatArrItem = (arr: Company[], action: Company[]): Company[] => {
  if (!action || !Array.isArray(action)) return [];
  
  const itemsToAdd: Company[] = [];
  const itemArrIds = arr?.map((item) => item?.id) || [];
  
  action.forEach((itemArr) => {
    if (!itemArr?.id || itemArrIds.includes(itemArr.id)) {
      return;
    }
    itemsToAdd.push(itemArr);
  });
  
  return itemsToAdd;
};

const newCompaniesListSlice = createSlice({
  name: "newCompaniesList",
  initialState,
  reducers: {
    /** Set companies list data with deduplication for card view */
    setNewCompanies: (state, action: PayloadAction<{ data: Company[], isCards?: boolean }>) => {
      const payload = action.payload;
      if (!payload?.isCards) {
        state.companiesList = payload?.data || [];
      } else {
        const uniqItems = noRepeatArrItem(state.companiesList, payload?.data || []);
        state.companiesList = [...state.companiesList, ...uniqItems];
      }
    },
    /** Add multiple companies to selection */
    setSelectAllCompanies: (state, action: PayloadAction<Company[]>) => {
      const payload = action.payload;
      const uniqItem = noRepeatArrItem(state.selectedCompanies, payload || []);
      state.selectedCompanies = [...state.selectedCompanies, ...uniqItem];
    },
    /** Clear all selected companies */
    clearSelectedCompanies: (state) => {
      state.selectedCompanies = [];
    },
    /** Toggle single company selection */
    setSelectedCompanies: (state, action: PayloadAction<Company>) => {
      const payload = action.payload;
      if (!payload) return;
      
      const companyIds = state.selectedCompanies.map((cmp) => cmp?.id);
      
      if (companyIds.includes(payload.id)) {
        state.selectedCompanies = state.selectedCompanies.filter(
          (item) => item?.id !== payload?.id
        );
      } else {
        state.selectedCompanies = [...state.selectedCompanies, payload];
      }
    },
    /** Set visible columns directly */
    setColumnsField: (state, action: PayloadAction<string[]>) => {
      state.columnsField = action.payload || [];
    },
    
    /** Add a column to visible columns */
    addColumn: {
      reducer: (state, action: PayloadAction<{ id: string }>) => {
        const { id } = action.payload;
        if (!id) return;
        
        // Create a new array with the column added
        const newColumns = [...state.columnsField];
        if (!newColumns.includes(id)) {
          newColumns.push(id);
        }
        state.columnsField = newColumns;
      },
      prepare: (id: string) => ({ payload: { id } })
    },
    
    /** Remove a column from visible columns */
    removeColumn: {
      reducer: (state, action: PayloadAction<{ id: string }>) => {
        const { id } = action.payload;
        if (!id) return;
        
        // Create a new array without the column
        state.columnsField = state.columnsField.filter(colId => colId !== id);
      },
      prepare: (id: string) => ({ payload: { id } })
    },
    /** Hide all columns */
    setHideAllColumns: (state) => {
      state.columnsField = [];
    },
    /** Show all columns */
    setShowAllColumns: (state) => {
      state.columnsField = getInitialColumns();
    },
    /** Set loading state */
    setIsLoadingCompanies: (state, action: PayloadAction<boolean>) => {
      const payload = action.payload;
      state.isCompaniesLoading = !!payload;
    },
    /** Remove companies by ID list */
    removeCompaniesByList: (state, action: PayloadAction<string[] | number[]>) => {
      const payload = action.payload;
      if (!payload || !Array.isArray(payload)) return;
      
      state.companiesList = state.companiesList.filter(
        (item) => !(payload as (string | number)[]).includes(item?.id as string)
      );
    },
    /** Toggle between card and table view */
    setIsCompaniesCards: (state, action: PayloadAction<boolean>) => {
      const payload = action.payload;
      state.isCompaniesCards = !!payload;
    },
    /** Load more companies for infinite scroll in card view */
    loadMoreForCompaniesCards: (state, action: PayloadAction<Company[]>) => {
      const payload = action.payload;
      if (!payload || !Array.isArray(payload)) return;
      
      const uniqItems = noRepeatArrItem(state.companiesList, payload);
      state.companiesList = [...state.companiesList, ...uniqItems];
    },
  },
});

export const newCompanieListActions = newCompaniesListSlice.actions;
export default newCompaniesListSlice;
